'use client';

import { PageInfoCGType, SurveyCGType } from '@/types/graphql/root';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import TripCard from './trip-card';
import { getOwnSurveys } from '@/utils/actions';
import SpinnerLoader1818 from '@svg/common/spinner_loader_18_18.svg';
import Luggage1616 from '@svg/main/my-travels/luggage_16_16.svg';

export default function MyTravelsTripList({
  initTrips,
  initPageInfo,
}: {
  initTrips: SurveyCGType[];
  initPageInfo: PageInfoCGType;
}) {
  const [trips, setTrips] = useState<SurveyCGType[]>(initTrips);
  const [pageInfo, setPageInfo] = useState<PageInfoCGType>(initPageInfo);
  const [serverLoading, setServerLoading] = useState(false);

  type TripStatus = 'upcoming' | 'ongoing' | 'previous';
  const tripsMapByStatus = useMemo(() => {
    const tripsMap = new Map<TripStatus, SurveyCGType[]>();
    trips.forEach((trip) => {
      const { fromDateTime, toDateTime } = trip;
      const now = dayjs();
      const fromDate = dayjs(fromDateTime);
      const toDate = dayjs(toDateTime);

      let status: TripStatus;

      if (now.isBefore(fromDate)) {
        status = 'upcoming';
      } else if (now.isAfter(toDate)) {
        status = 'previous';
      } else {
        status = 'ongoing';
      }

      if (tripsMap.has(status)) {
        tripsMap.set(status, [...tripsMap.get(status)!, trip]);
      } else {
        tripsMap.set(status, [trip]);
      }
    });

    return tripsMap;
  }, [trips]);

  const previousTripsMapByYear = useMemo(() => {
    const previousTripsMap = new Map<number, SurveyCGType[]>();
    tripsMapByStatus.get('previous')?.forEach((trip) => {
      const { toDateTime } = trip;
      const year = new Date(toDateTime).getFullYear();
      if (previousTripsMap.has(year)) {
        previousTripsMap.set(year, [...previousTripsMap.get(year)!, trip]);
      } else {
        previousTripsMap.set(year, [trip]);
      }
    });

    return previousTripsMap;
  }, [tripsMapByStatus]);

  const onClickMoreTrips = useCallback(async () => {
    try {
      setServerLoading(true);
      const offset = (pageInfo.offset ?? 0) + (pageInfo.limit ?? 10);
      const surveyConnection = await getOwnSurveys(offset, pageInfo.limit ?? 0);
      if (surveyConnection?.pageInfo) setPageInfo(surveyConnection.pageInfo);
      if (surveyConnection?.edges) setTrips([...trips, ...surveyConnection.edges.map((e) => e.node)]);
    } catch (error) {
      console.error(error);
    } finally {
      setServerLoading(false);
    }
  }, [pageInfo, trips]);

  return (
    <div className='w-full'>
      {tripsMapByStatus.get('ongoing') && (
        <div className='bg-new-point-50'>
          <div className='px-20pxr pt-24pxr pb-16pxr'>
            <h5 className='heading-14-b flex items-center text-new-blue-500'>
              <Luggage1616 />
              진행중인 여행
            </h5>
          </div>

          <div className='px-20pxr pb-18pxr flex flex-col gap-20pxr'>
            {tripsMapByStatus
              .get('ongoing')
              ?.map((trip) => (
                <TripCard
                  className='bg-white border border-new-point-300 rounded-10pxr shadow-[0px_0px_30px_0px_rgba(168,187,198,0.30)]'
                  key={`trip-card_` + trip.id}
                  trip={trip}
                  setTrips={setTrips}
                />
              ))}
          </div>
        </div>
      )}

      {tripsMapByStatus.get('upcoming') && (
        <>
          <div className='w-full h-10pxr bg-gray-100 border-y border-gray-300'></div>
          <div className='px-20pxr pt-24pxr pb-16pxr'>
            <h5 className='heading-14-b text-custom-black'>다가오는 여행</h5>
          </div>

          <div className='px-20pxr pb-18pxr flex flex-col gap-20pxr'>
            {tripsMapByStatus
              .get('upcoming')
              ?.map((trip) => <TripCard key={`trip-card_` + trip.id} trip={trip} setTrips={setTrips} />)}
          </div>
        </>
      )}

      {tripsMapByStatus.get('previous') && (
        <>
          <div className='w-full h-10pxr bg-gray-100 border-y border-gray-300'></div>
          <div className='px-20pxr pt-24pxr pb-16pxr'>
            <h5 className='heading-14-b text-custom-black'>지난 여행</h5>
          </div>
          {[...previousTripsMapByYear.keys()].map((year) => (
            <React.Fragment key={'yearbox_' + year}>
              <div className='px-20pxr'>
                <p className='text-gray-700 sub-heading-14-m mb-16pxr'>{year}년</p>
              </div>
              <div className='px-20pxr pb-18pxr flex flex-col gap-20pxr'>
                {previousTripsMapByYear
                  .get(year)
                  ?.map((trip) => <TripCard key={`trip-card_` + trip.id} trip={trip} setTrips={setTrips} />)}
              </div>
            </React.Fragment>
          ))}
        </>
      )}
      {serverLoading && (
        <div className='w-full mt-6pxr mb-24pxr flex items-center justify-center h-20pxr'>
          <SpinnerLoader1818 className='animate-spin' />
        </div>
      )}
      {!serverLoading && pageInfo.hasNextPage && (
        <button
          className='w-full mt-6pxr mb-24pxr button-16-m text-center text-primary h-20pxr'
          onClick={() => {
            onClickMoreTrips();
          }}
        >
          더보기
        </button>
      )}
    </div>
  );
}
